import { useEffect, useRef } from "react";

import { reaction } from "mobx";
import { observer } from "mobx-react-lite";

import { AWARENESS_POINTER_MOVE_THROTTLE_INTERVAL } from "@/components/awareness/constants";
import { Icon } from "@/components/icons/Icon";

import type { AwarenessModel } from "@/models/AwarenessModel";
import type { Editor } from "@tiptap/core";

type Props = {
  // user: UserState;
  awareness: AwarenessModel;
  clientId: string;
  tiptap: Editor;
};

export const AwarenessPointer = observer(function AwarenessPointer({
  awareness,
  clientId,
  tiptap,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    // avoid component re-rendering on every pointer move by using mobx reaction
    const dispose = reaction(
      () => {
        const pointer = awareness.pointer(clientId);
        return [pointer?.left, pointer?.top, pointer?.rel] as const;
      },
      ([left, top, rel]) => {
        if (rel) {
          requestAnimationFrame(() => {
            if (!ref.current) return;

            const coords = tiptap.view.coordsAtPos(rel.pos);
            const newLeft = coords.left + rel.dx;
            const newTop = coords.top + rel.dy;

            ref.current.style.transform = `translate(${newLeft}px, ${newTop}px)`;
          });
        } else {
          ref.current!.style.transform = `translate(${left}px, ${top}px)`;
        }
      },
    );

    return dispose;
  }, [awareness, tiptap, clientId]);

  const user = awareness.userState(clientId);

  return (
    <div
      ref={ref}
      aria-hidden
      data-testid="awareness-pointer"
      className="absolute left-0 top-0 pointer-events-none"
      style={{
        transition: `transform ${AWARENESS_POINTER_MOVE_THROTTLE_INTERVAL}ms linear`,
      }}
    >
      <Icon name="AwarenessPointer" className={`fill-${user?.twColor}-500`} />
    </div>
  );
});
